import { useEffect, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { MapPin, Pencil, Trash2, Plus, RefreshCw, X } from "lucide-react";
import { SortableTH, useSortableData, EmptyState } from "@/components/SortableTable";
import TableSkeleton from "@/components/TableSkeleton";
import RowActionsMenu from "@/components/RowActionsMenu";
import Banner from "@/components/Banner";
import { INDIA_STATES } from "@/lib/indiaStates";

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

const EMPTY = { name: "", address: "", city: "", state: "", country: "India", is_active: true };

export default function Locations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState("");

  const { items: sortedItems, sortBy, sortDir, requestSort } = useSortableData(items, { initialKey: "name" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/locations");
      setItems(r.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setErr("");
    setOpen(true);
  };
  const startEdit = (loc) => {
    setEditing(loc);
    setForm({
      name: loc.name || "",
      address: loc.address || "",
      city: loc.city || "",
      state: loc.state || "",
      country: loc.country || "India",
      is_active: loc.is_active !== false,
    });
    setErr("");
    setOpen(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!(form.name || "").trim()) {
      setErr("Name is required");
      return;
    }
    if (!(form.state || "").trim()) {
      setErr("State is required (used for Professional Tax)");
      return;
    }
    try {
      if (editing) await api.put(`/locations/${editing.id}`, form);
      else await api.post("/locations", form);
      setOpen(false);
      await load();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed");
    }
  };
  const onDelete = async (loc) => {
    if (!window.confirm(`Delete '${loc.name}'?`)) return;
    try {
      await api.delete(`/locations/${loc.id}`);
      await load();
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <AdminLayout title="Locations / Branches" breadcrumbs={["Home", "Organization", "Locations"]}>
      <div className="bg-white rounded-lg shadow border border-slate-200">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2 text-slate-700 font-semibold">
            <MapPin className="h-4 w-4" /> Locations
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="p-2 text-slate-500 hover:bg-slate-100 rounded" data-testid="locations-refresh">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              onClick={startCreate}
              data-testid="add-location-btn"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded"
            >
              <Plus className="h-4 w-4" /> Add Location
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <SortableTH columnKey="name" active={sortBy} dir={sortDir} onClick={requestSort}>Name</SortableTH>
                <SortableTH columnKey="city" active={sortBy} dir={sortDir} onClick={requestSort}>City</SortableTH>
                <SortableTH columnKey="state" active={sortBy} dir={sortDir} onClick={requestSort}>State</SortableTH>
                <SortableTH columnKey="country" active={sortBy} dir={sortDir} onClick={requestSort}>Country</SortableTH>
                <SortableTH columnKey="is_active" active={sortBy} dir={sortDir} onClick={requestSort}>Status</SortableTH>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-slate-600 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <TableSkeleton rows={4} cols={6} />
              ) : sortedItems.length === 0 ? (
                <EmptyState colSpan={6} message="No locations yet" />
              ) : (
                sortedItems.map((l) => (
                  <tr key={l.id} data-testid={`location-row-${l.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">{l.name}</td>
                    <td className="px-4 py-3 text-slate-600">{l.city}</td>
                    <td className="px-4 py-3 text-slate-600">{l.state}</td>
                    <td className="px-4 py-3 text-slate-600">{l.country}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${l.is_active ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                        {l.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <RowActionsMenu
                        testid={`location-actions-${l.id}`}
                        items={[
                          { label: "Edit", icon: Pencil, onClick: () => startEdit(l), testid: `edit-location-${l.id}` },
                          { label: "Delete", icon: Trash2, danger: true, onClick: () => onDelete(l), testid: `delete-location-${l.id}` },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-lg" data-testid="location-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h3 className="font-semibold text-slate-800">{editing ? "Edit Location" : "Add Location"}</h3>
              <button type="button" onClick={() => setOpen(false)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <Banner variant="error">{err}</Banner>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Name *</label>
                <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="loc-name" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Address</label>
                <input className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="loc-address" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">City</label>
                  <input className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="loc-city" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">State *</label>
                  <select
                    className={inputCls}
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    required
                    data-testid="loc-state"
                  >
                    <option value="">Select…</option>
                    {INDIA_STATES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Country</label>
                  <input className={inputCls} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="loc-active" />
                    Active
                  </label>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-slate-200 rounded">Cancel</button>
              <button type="submit" className="px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded" data-testid="loc-save">Save</button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
